import {SortingDropdown} from '@github-ui/dashboard-sorting-dropdown/SortingDropdown'
import {announce} from '@github-ui/aria-live'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {ListView} from '@github-ui/list-view'
import {ListItem} from '@github-ui/list-view/ListItem'
import {ListItemDescription} from '@github-ui/list-view/ListItemDescription'
import {ListItemLeadingContent} from '@github-ui/list-view/ListItemLeadingContent'
import {ListItemLeadingVisual} from '@github-ui/list-view/ListItemLeadingVisual'
import {ListItemMainContent} from '@github-ui/list-view/ListItemMainContent'
import {ListItemMetadata} from '@github-ui/list-view/ListItemMetadata'
import {ListItemTitle} from '@github-ui/list-view/ListItemTitle'
import {ListViewDensityToggle} from '@github-ui/list-view/ListViewDensityToggle'
import {ListViewMetadata, type ListViewMetadataProps} from '@github-ui/list-view/ListViewMetadata'
import {useListViewVariant} from '@github-ui/list-view/ListViewVariantContext'
import {Link as RouterLink} from '@github-ui/react-router'
import {GlobalCommands, ScopedCommands} from '@github-ui/ui-commands'
import {SkeletonBox} from '@primer/react/experimental'
import {clsx} from 'clsx'
import {memo, type ComponentProps, type MouseEvent, type RefObject, useEffect, useRef} from 'react'

import styles from '../shared/SharedListContainer.module.css'
import compactStyles from '../shared/CompactListItem.module.css'
import {ListViewSectionFilterLink} from '@github-ui/list-view/ListViewSectionFilterLink'
import {useAnalytics} from '@github-ui/use-analytics'
import {DEFAULT_SORTING_KEY, defaultSortingOptions, isUpdatedSort} from '../shared/consts/sorting-consts'
import type {EnrichedPullListItem} from './types/list-types'
import {PullsListItem, type PullsListItemLinkProps} from './PullsListItem'
import itemStyles from './PullsListItem.module.css'
import type {AuthorFilterLinkProps} from './PullsListItemTimestampContainer'
import {SharedDashboardBlankslate} from '../shared/SharedDashboardBlankslate'
import type {EmptyListState} from '../shared/types/blankslate-types'
import {DisplayMode, type PageInfo, type StateCountPageInfo, type StateFilter} from '../shared/types/list-types'

const noop = () => {}

function hasStateCounts(pageInfo: PageInfo): pageInfo is StateCountPageInfo {
  return 'openCount' in pageInfo && 'closedCount' in pageInfo
}

const NOOP_FOCUS_COMMANDS = {
  'shared-dashboard-lists:focus-next-pull': noop,
  'shared-dashboard-lists:focus-previous-pull': noop,
} as const

export interface PullsListSelection {
  selectedPullNumbers: ReadonlySet<number>
  disabled?: boolean
  selectAllCheckboxRef?: RefObject<HTMLInputElement | null>
  onPullSelect: (pullNumber: number, isSelected: boolean) => void
  onToggleSelectAll: (isSelected: boolean) => void
  actions?: ListViewMetadataProps['actions']
}

type StateFilterRouterLinkProps = Omit<ComponentProps<typeof RouterLink>, 'children' | 'title'>

type PullsListStateFilters =
  | {kind: 'hidden'}
  | {
      kind: 'visible'
      selected?: StateFilter
      counts?: {open: number; closed: number}
      getLinkProps: (state: StateFilter) => StateFilterRouterLinkProps
    }

const NO_FOCUS_COMMANDS = {}

const PullsListLoadingItem = memo(function PullsListLoadingItemComponent() {
  const {variant} = useListViewVariant()
  const isCompact = variant === DisplayMode.Compact

  const metadata = isCompact
    ? [
        <ListItemMetadata key="review" className={`${compactStyles.metadataCell} ${compactStyles.metadataReview}`}>
          <SkeletonBox width="48px" height="16px" />
        </ListItemMetadata>,
        <ListItemMetadata key="status" className={`${compactStyles.metadataCell} ${compactStyles.metadataStatus}`}>
          <SkeletonBox width="40px" height="16px" />
        </ListItemMetadata>,
        <ListItemMetadata key="comments" className={`${compactStyles.metadataCell} ${compactStyles.metadataUtility}`}>
          <SkeletonBox width="24px" height="16px" />
        </ListItemMetadata>,
        <ListItemMetadata
          key="assignees"
          className={`${compactStyles.metadataCell} ${compactStyles.metadataAssignees}`}
        >
          <SkeletonBox width="24px" height="24px" style={{borderRadius: 'var(--borderRadius-full)'}} />
        </ListItemMetadata>,
      ]
    : [
        <ListItemMetadata key="comments">
          <SkeletonBox width="32px" height="16px" />
        </ListItemMetadata>,
      ]

  return (
    <ListItem
      {...{inert: true}}
      aria-hidden="true"
      className={isCompact ? itemStyles.listItemCompact : itemStyles.listItem}
      metadata={metadata}
      metadataContainerClassName={isCompact ? compactStyles.metadataContainer : undefined}
      title={
        <ListItemTitle value="">
          <SkeletonBox height={isCompact ? '21px' : '24px'} width={isCompact ? '180px' : '40%'} />
        </ListItemTitle>
      }
    >
      <ListItemLeadingContent>
        <ListItemLeadingVisual>
          <SkeletonBox height="16px" width="16px" style={{borderRadius: 'var(--borderRadius-full)'}} />
        </ListItemLeadingVisual>
      </ListItemLeadingContent>
      <ListItemMainContent>
        <ListItemDescription
          className={clsx(isCompact ? compactStyles.leftMetadata : itemStyles.description, {
            [itemStyles.loadingDescription]: !isCompact,
            [itemStyles.loadingDescriptionCompact]: isCompact,
          })}
        >
          <SkeletonBox height="16px" width={isCompact ? '140px' : '55%'} />
          {!isCompact ? (
            <SkeletonBox className={itemStyles.loadingDescriptionSecondary} height="16px" width="35%" />
          ) : null}
        </ListItemDescription>
      </ListItemMainContent>
    </ListItem>
  )
})

function PullsListLoadingItems({numItems}: {numItems: number}) {
  return Array.from({length: numItems}, (_, index) => <PullsListLoadingItem key={`pulls-list-skeleton-${index}`} />)
}

interface StateFilterLinkProps {
  state: StateFilter
  title: string
  count: number | undefined
  isSelected: boolean
  routerLinkProps: StateFilterRouterLinkProps
}

function StateFilterLink({state, title, count, isSelected, routerLinkProps}: StateFilterLinkProps) {
  const {sendAnalyticsEvent} = useAnalytics()
  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    sendAnalyticsEvent('pull-requests.click', 'STATE_FILTER', {state})
    routerLinkProps.onClick?.(event)
  }

  return (
    <ListViewSectionFilterLink
      {...routerLinkProps}
      title={title}
      isSelected={isSelected}
      count={count?.toLocaleString('en-US')}
      onClick={handleClick}
    />
  )
}

type PullsListProps = {
  pulls: EnrichedPullListItem[]
  pageInfo: PageInfo | StateCountPageInfo
  compactMode: boolean
  onCompactModeChange: (value: boolean) => void
  onSortingItemSelect: (key: string) => void
  emptyListState: EmptyListState
  sortingItemSelected?: string
  stateFilters: PullsListStateFilters
  onFilterSelect?: (type: 'repo' | 'author' | 'label' | 'assignee', value: string) => void
  onReviewFilterSelect?: ComponentProps<typeof PullsListItem>['onReviewFilterSelect']
  isLoading?: boolean
  isDeferredLoading?: boolean
  suppressEmptyState?: boolean
  isAgentAttributionEnabled?: boolean
  showUnreadIndicator?: boolean
  defaultSortingKey?: string
  showRepository?: boolean
  actionBarItems?: ComponentProps<typeof ListViewMetadata>['actionBarItems']
  getTitleLinkProps?: (pull: EnrichedPullListItem) => PullsListItemLinkProps | undefined
  selection?: PullsListSelection
  getAuthorLinkProps?: (login: string) => AuthorFilterLinkProps | undefined
}

type PullsListRowProps = Omit<ComponentProps<typeof PullsListItem>, 'onSelect' | 'titleLinkProps'> & {
  onPullSelect?: PullsListSelection['onPullSelect']
  getTitleLinkProps?: PullsListProps['getTitleLinkProps']
}

const PullsListRow = memo(function PullsListRowComponent({
  pull,
  onPullSelect,
  getTitleLinkProps,
  ...itemProps
}: PullsListRowProps) {
  const handleSelect = (isSelected: boolean) => onPullSelect?.(pull.number, isSelected)

  return (
    <PullsListItem
      {...itemProps}
      pull={pull}
      onSelect={onPullSelect ? handleSelect : undefined}
      titleLinkProps={getTitleLinkProps?.(pull)}
    />
  )
})

const EMPTY_SELECTED_PULL_NUMBERS = new Set<number>()

export function PullsList({
  pulls,
  pageInfo,
  compactMode,
  onCompactModeChange,
  sortingItemSelected,
  onSortingItemSelect,
  stateFilters,
  emptyListState,
  onFilterSelect,
  onReviewFilterSelect,
  isLoading = false,
  isDeferredLoading,
  suppressEmptyState = false,
  isAgentAttributionEnabled,
  showUnreadIndicator = true,
  defaultSortingKey = DEFAULT_SORTING_KEY,
  showRepository = true,
  actionBarItems,
  getTitleLinkProps,
  selection,
  getAuthorLinkProps,
}: PullsListProps) {
  const declutter = isFeatureEnabled('repo_pulls_dashboard_declutter')
  // The default sort key is serialized as an empty string, so resolve it before checking.
  const isUpdatedSortSelected = isUpdatedSort(sortingItemSelected || defaultSortingKey)
  const resultText = pageInfo.totalCount === 1 ? 'result' : 'results'
  const formattedTotalCount = pageInfo.totalCount.toLocaleString('en-US')

  const visibleStateFilters = stateFilters.kind === 'visible' ? stateFilters : undefined
  const stateItemSelected = visibleStateFilters?.selected
  const pageInfoStateCounts = hasStateCounts(pageInfo)
    ? {open: pageInfo.openCount, closed: pageInfo.closedCount}
    : undefined
  const stateFilterCounts = visibleStateFilters?.counts ?? pageInfoStateCounts
  const showLoadingItems = isLoading && pulls.length === 0
  const loadingItemCount = pageInfo.totalCount > 0 ? Math.min(pageInfo.totalCount, 5) : 5
  const hasAnnouncedLoadingRef = useRef(false)

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- background refreshes and route changes do not have a local event handler.
    if (!isLoading) {
      hasAnnouncedLoadingRef.current = false
      return
    }
    if (hasAnnouncedLoadingRef.current) return

    hasAnnouncedLoadingRef.current = true
    announce('Loading pull requests')
  }, [isLoading])

  const listRef = useRef<HTMLElement | undefined>(undefined)
  const selectedPullNumbers = selection?.selectedPullNumbers ?? EMPTY_SELECTED_PULL_NUMBERS
  const anyPullsSelected = selectedPullNumbers.size > 0
  const focusCommandsEnabled = !isLoading && pulls.length > 0
  const actionBarProps: ComponentProps<typeof ListViewMetadata> =
    anyPullsSelected && selection?.actions
      ? {actions: selection.actions, actionsLabel: 'Pull request actions'}
      : actionBarItems
        ? {
            actionBarItems: (
              <>
                {actionBarItems}
                <SortingDropdown
                  sortingItemSelected={sortingItemSelected || ''}
                  onSortingItemSelect={onSortingItemSelect}
                  defaultSortingKey={defaultSortingKey}
                  sortingOptions={defaultSortingOptions}
                  directionRestrictedKeys={['relevance']}
                  sortFieldsRequireDescSuffix
                  isActionBarItem
                />
              </>
            ),
            actionsLabel: 'Pull request filters',
            actionsClassName: styles.primerActionBar,
          }
        : {}

  const focusTabbablePull = () => {
    const tabbableItem = listRef.current?.querySelector('[tabindex="0"]')
    if (tabbableItem instanceof HTMLElement) {
      tabbableItem.focus()
    }
  }

  const listView = (
    <ListView
      title={showLoadingItems ? 'Loading pull requests' : 'Pull requests'}
      titleHeaderTag="h2"
      className={styles.listContainer}
      listRef={listRef}
      onVariantChange={value => onCompactModeChange(value === DisplayMode.Compact)}
      variant={compactMode ? DisplayMode.Compact : DisplayMode.Default}
      aria-busy={isLoading}
      isSelectable={Boolean(selection)}
      isSelectionDisabled={selection?.disabled}
      selectedCount={selectedPullNumbers.size}
      totalCount={pageInfo.totalCount}
      singularUnits="pull request"
      pluralUnits="pull requests"
      metadata={
        <ListViewMetadata
          onToggleSelectAll={selection?.onToggleSelectAll}
          selectAllCheckboxRef={selection?.selectAllCheckboxRef}
          title={
            anyPullsSelected ||
            (visibleStateFilters && (stateItemSelected === 'open' || stateItemSelected === 'closed')) ? undefined : (
              <span data-testid="results-count">
                {formattedTotalCount} {resultText}
              </span>
            )
          }
          sectionFilters={
            !anyPullsSelected && visibleStateFilters ? (
              <>
                <StateFilterLink
                  state="open"
                  title="Open"
                  count={stateFilterCounts?.open}
                  isSelected={stateItemSelected === 'open'}
                  routerLinkProps={visibleStateFilters.getLinkProps('open')}
                />
                <StateFilterLink
                  state="closed"
                  title="Closed"
                  count={stateFilterCounts?.closed}
                  isSelected={stateItemSelected === 'closed'}
                  routerLinkProps={visibleStateFilters.getLinkProps('closed')}
                />
              </>
            ) : undefined
          }
          densityToggle={<ListViewDensityToggle />}
          {...actionBarProps}
        >
          {!anyPullsSelected && !actionBarItems && (
            <div className="mr-1">
              <SortingDropdown
                sortingItemSelected={sortingItemSelected || ''}
                onSortingItemSelect={onSortingItemSelect}
                defaultSortingKey={defaultSortingKey}
                sortingOptions={defaultSortingOptions}
                directionRestrictedKeys={['relevance']}
                sortFieldsRequireDescSuffix
              />
            </div>
          )}
        </ListViewMetadata>
      }
    >
      {showLoadingItems ? (
        <PullsListLoadingItems numItems={loadingItemCount} />
      ) : pulls.length > 0 ? (
        pulls.map(pull => (
          <PullsListRow
            key={pull.id}
            pull={pull}
            isSelected={selectedPullNumbers.has(pull.number)}
            onPullSelect={selection?.onPullSelect}
            onFilterSelect={onFilterSelect}
            onReviewFilterSelect={onReviewFilterSelect}
            isDeferredLoading={isDeferredLoading}
            isAgentAttributionEnabled={isAgentAttributionEnabled}
            showUnreadIndicator={showUnreadIndicator}
            declutter={declutter}
            isUpdatedSort={isUpdatedSortSelected}
            showRepository={showRepository}
            getTitleLinkProps={getTitleLinkProps}
            getAuthorLinkProps={getAuthorLinkProps}
          />
        ))
      ) : suppressEmptyState ? null : (
        <li>
          <SharedDashboardBlankslate dashboardType="pull-requests" variant="empty" emptyListState={emptyListState} />
        </li>
      )}
    </ListView>
  )

  return (
    <>
      <GlobalCommands
        commands={
          focusCommandsEnabled
            ? {
                'shared-dashboard-lists:focus-next-pull': focusTabbablePull,
                'shared-dashboard-lists:focus-previous-pull': focusTabbablePull,
              }
            : NO_FOCUS_COMMANDS
        }
      />
      {/* if focus is already in the list, delegate keyboard navigation to ListView */}
      <ScopedCommands commands={focusCommandsEnabled ? NOOP_FOCUS_COMMANDS : NO_FOCUS_COMMANDS}>
        {listView}
      </ScopedCommands>
    </>
  )
}
